"use client";
import CTALink from "@/components/CTA/variants/CtaLink";
import type { ProcessedCourse } from "@/common/course/types";
import useClientEnrollments from "@/lib/partner-api/useClientEnrollments";
import useClientEntitlements from "@/lib/partner-api/useClientEntitlements";
import type { Program } from "@/common/program/types";
import { getBrandString } from "@/utils/tagular/helpers";
import useCertificateCTAText from "@/monarch/rules/certificateCTA/useCertificateCTAText";
import { Skeleton } from "@/components/ui/skeleton";
import getProgramFromSubject from "../../utils/getSubjectFromProgram";
import useEmailOptIn from "../../context/ProgramDetailContext/hooks/useEmailOptIn";
import useEnrollmentUrl from "@/lib/program/utils/programEnroll/useEnrollmentUrl";
import { cn } from "@/lib/utils";
import useTranslations from "@/i18n/useTranslations";

// Base styles for program enrollment button
const baseStyles = "mx-auto py-2 px-6 w-full lg:h-auto text-[16px]";

export type ProgramEnrollButtonProps = {
  courses: ProcessedCourse[];
  programData: Program;
  skus: string[];
  additionalClickEvent?: (updatedText: string) => void;
  location?: string;
  enableTracking?: boolean;
  buttonClass?: string;
  ctaText?: string;
  conversionType?: string;
};

export default function ProgramEnrollButtonWithCTATest(
  props: ProgramEnrollButtonProps,
) {
  const { isLoading, ctaText, conversionType } = useCertificateCTAText({
    isProgramEnrollable: true,
  }); //This is only ever rendered if the program is enrollable

  if (isLoading) {
    return (
      <Skeleton className="h-12 min-w-44 rounded-full w-full" role="status" />
    );
  }

  return (
    <ProgramEnrollButton
      ctaText={ctaText}
      conversionType={conversionType}
      {...props}
    />
  );
}

export const ProgramEnrollButton = ({
  courses,
  programData,
  skus,
  additionalClickEvent,
  location,
  enableTracking = false,
  buttonClass = "",
  ctaText,
  conversionType,
}: ProgramEnrollButtonProps) => {
  const tCourse = useTranslations("Course");
  const { enrollments } = useClientEnrollments();
  const { entitlements } = useClientEntitlements();
  const { emailOptIn } = useEmailOptIn();

  const programSubject = getProgramFromSubject(programData);

  const { productType } = programData;
  const brand = getBrandString(programData);

  //In the old prospectus repo, there was no special url for webview context, it was the same external ecommerce link.
  //So we're keeping the new url generation, but sending users to the browser for now
  const enrollmentUrl = useEnrollmentUrl(
    programData,
    courses,
    skus,
    enrollments,
    entitlements,
    emailOptIn,
  );

  const label =
    !ctaText || ctaText === "Earn a certificate"
      ? tCourse("earnACertificate")
      : ctaText;

  const handleClickWithText = () => {
    additionalClickEvent?.(label);
  };

  return (
    <CTALink
      label={label}
      type="brand"
      url={enrollmentUrl}
      size="lg"
      className={cn(baseStyles, buttonClass)}
      enable_tracking={enableTracking}
      conversionTrackingInfo={{
        customName: conversionType,
        productList: courses.map((course) => ({
          productName: course.productName,
          brand,
          variant: productType,
          category: programSubject,
          id: course.key,
          externalId: course.activeCourseRun?.key,
        })),
      }}
      location={location}
      additionalClickEvent={handleClickWithText}
      whitespace="whitespace-nowrap"
    />
  );
};
